import {
  ReportSection,
  SavedReportSection,
  ReportChartType,
  UserCustomisableChartConfig,
  ReportChartConfig,
} from 'app/modules/reportDashboards/models';
import { hasPermissionChecker } from 'app/modules/permissions/utils';

export const getSavableReportDashboardConfig = (
  config: ReportSection[],
): SavedReportSection[] =>
  config.map((section) => ({
    id: section.id,
    charts: section.charts.map((chart) => ({
      id: chart.id,
      enabled: chart.enabled,
      chartType: chart.chartType,
    })),
  }));

export const mergeReportDashboardConfig = (
  savedConfig: SavedReportSection[],
  defaultConfig: ReportSection[],
  userPermissions: string[],
): ReportSection[] => {
  return defaultConfig
    .filter((section) => {
      if (section.permissions?.length) {
        // Show section only if user has permission required by section
        return hasPermissionChecker(userPermissions, section.permissions);
      }

      return true;
    })
    .map((section): ReportSection => {
      const savedSection = savedConfig.find((s) => s.id === section.id);

      switch (section.id) {
        case 'kpis': {
          const { permissions, ...rest } = section;
          if (savedSection) {
            return {
              ...rest,
              charts: filterChartsByPermissions(
                mergeCharts(section.charts, savedSection.charts),
                userPermissions,
              ),
            };
          }
          return {
            ...rest,
            charts: filterChartsByPermissions(section.charts, userPermissions),
          };
        }
        case 'alerts': {
          const { permissions, ...rest } = section;
          if (savedSection) {
            return {
              ...rest,
              charts: filterChartsByPermissions(
                mergeCharts(section.charts, savedSection.charts),
                userPermissions,
              ),
            };
          }
          return {
            ...rest,
            charts: filterChartsByPermissions(section.charts, userPermissions),
          };
        }
        case 'cases': {
          const { permissions, ...rest } = section;
          if (savedSection) {
            return {
              ...rest,
              charts: filterChartsByPermissions(
                mergeCharts(section.charts, savedSection.charts),
                userPermissions,
              ),
            };
          }
          return {
            ...rest,
            charts: filterChartsByPermissions(section.charts, userPermissions),
          };
        }
        case 'sars': {
          const { permissions, ...rest } = section;
          if (savedSection) {
            return {
              ...rest,
              charts: filterChartsByPermissions(
                mergeCharts(section.charts, savedSection.charts),
                userPermissions,
              ),
            };
          }
          return {
            ...rest,
            charts: filterChartsByPermissions(section.charts, userPermissions),
          };
        }
        default: {
          const exhaustivenessCheck: never = section;
          return exhaustivenessCheck;
        }
      }
    });
};

const mergeCharts = <T extends ReportChartConfig>(
  defaultCharts: T[],
  savedCharts: UserCustomisableChartConfig[],
): T[] => {
  const defaultChartsMap = new Map(
    defaultCharts.map((chart) => [chart.id, chart]),
  );

  const mergedCharts = savedCharts
    .map((savedChart) => {
      const defaultChart = defaultChartsMap.get(savedChart.id);
      if (!defaultChart) return null;

      defaultChartsMap.delete(savedChart.id);

      const mergedChart = { ...defaultChart, ...savedChart };
      const mergedChartType = savedChart.chartType ?? defaultChart.chartType;
      if (mergedChartType !== undefined) {
        mergedChart.chartType = mergedChartType;
      }

      return mergedChart;
    })
    .filter((chart): chart is T => chart !== null);

  return [...mergedCharts, ...Array.from(defaultChartsMap.values())];
};

const filterChartsByPermissions = <T extends ReportChartConfig>(
  charts: T[],
  userPermissions: string[],
) => {
  return charts
    .filter((chart) => {
      if (chart.permissions?.length) {
        // Show chart only if user has permission required by chart
        return hasPermissionChecker(userPermissions, chart.permissions);
      }
      return true;
    })
    .map((chart) => {
      const { permissions, ...rest } = chart;
      return rest;
    })
    .filter((chart): chart is T => chart !== null);
};

export const updateChartTypeInConfig = (
  config: ReportSection[],
  sectionId: ReportSection['id'],
  chartId: string,
  newChartType: ReportChartType,
): ReportSection[] => {
  return config.map((sectionConfig) => {
    if (sectionConfig.id === sectionId) {
      return {
        ...sectionConfig,
        charts: sectionConfig.charts.map((chart) =>
          chart.id === chartId ? { ...chart, chartType: newChartType } : chart,
        ),
      };
    }
    return sectionConfig;
  });
};
